import { useState, useCallback } from 'react';
import {
  addPhoneToCustomer,
  updatePhone,
  deletePhone,
  setPrimaryPhone,
} from '../api/customerPhonesApi';
import type { PhoneInput, CustomerPhoneSummary } from '../types';

interface UseCustomerPhonesOptions {
  customerId: string;
  onSuccess?: () => void;
}

interface UseCustomerPhonesReturn {
  isAdding: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  error: string | null;
  addPhone: (input: PhoneInput) => Promise<string | null>;
  updatePhoneById: (phoneId: string, input: Partial<PhoneInput>) => Promise<boolean>;
  deletePhoneById: (phoneId: string) => Promise<boolean>;
  setAsPrimary: (phoneId: string) => Promise<boolean>;
  clearError: () => void;
}

/**
 * Hook for managing customer phones (add, update, delete, set primary)
 */
export function useCustomerPhones({
  customerId,
  onSuccess,
}: UseCustomerPhonesOptions): UseCustomerPhonesReturn {
  const [isAdding, setIsAdding] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addPhone = useCallback(
    async (input: PhoneInput): Promise<string | null> => {
      setIsAdding(true);
      setError(null);

      try {
        const phoneId = await addPhoneToCustomer(customerId, input);
        onSuccess?.();
        return phoneId;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to add phone';
        setError(message);
        console.error('[useCustomerPhones] Add error:', err);
        return null;
      } finally {
        setIsAdding(false);
      }
    },
    [customerId, onSuccess]
  );

  const updatePhoneById = useCallback(
    async (phoneId: string, input: Partial<PhoneInput>): Promise<boolean> => {
      setIsUpdating(true);
      setError(null);

      try {
        await updatePhone(phoneId, input);
        onSuccess?.();
        return true;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to update phone';
        setError(message);
        console.error('[useCustomerPhones] Update error:', err);
        return false;
      } finally {
        setIsUpdating(false);
      }
    },
    [onSuccess]
  );

  const deletePhoneById = useCallback(
    async (phoneId: string): Promise<boolean> => {
      setIsDeleting(true);
      setError(null);

      try {
        await deletePhone(phoneId);
        onSuccess?.();
        return true;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to delete phone';
        setError(message);
        console.error('[useCustomerPhones] Delete error:', err);
        return false;
      } finally {
        setIsDeleting(false);
      }
    },
    [onSuccess]
  );

  const setAsPrimary = useCallback(
    async (phoneId: string): Promise<boolean> => {
      setIsUpdating(true);
      setError(null);

      try {
        await setPrimaryPhone(phoneId);
        onSuccess?.();
        return true;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to set primary phone';
        setError(message);
        console.error('[useCustomerPhones] Set primary error:', err);
        return false;
      } finally {
        setIsUpdating(false);
      }
    },
    [onSuccess]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    isAdding,
    isUpdating,
    isDeleting,
    error,
    addPhone,
    updatePhoneById,
    deletePhoneById,
    setAsPrimary,
    clearError,
  };
}

/**
 * Helper to find primary phone from phone list
 */
export function getPrimaryPhone(
  phones: CustomerPhoneSummary[]
): CustomerPhoneSummary | undefined {
  return phones.find((p) => p.is_primary);
}

/**
 * Helper to sort phones with primary first
 */
export function sortPhonesPrimaryFirst(
  phones: CustomerPhoneSummary[]
): CustomerPhoneSummary[] {
  return [...phones].sort((a, b) => {
    if (a.is_primary && !b.is_primary) return -1;
    if (!a.is_primary && b.is_primary) return 1;
    return 0;
  });
}
